import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { TripPlannerPax } from '@apps/main/modules/recipe/pax';
import type { TripPlannerRoomPax } from '@apps/main/modules/recipe/pax';
import type { FerryIngredient } from '@apps/main/modules/recipe/ingredients';

import type { Pax } from '@libs/trip-planner-interfaces';

import { useBrowserStore } from '../browserStore';
import { useStatsigEventsStore } from '../statsigEvents/statsigEventsStore';
import type { RoomPaxConfig } from '../../../modules/paxSelector';
import type { CoreEventSource } from '../../models';
import { useRecipeStore } from '../recipe/recipeStore';

import {
  getFormattedChildrenAges,
  getFormattedPax,
  savePaxInKeyValueStorage,
} from './paxStoreLogic';

export const usePaxStore = defineStore('PaxStore', () => {
  const browserStore = useBrowserStore();
  const recipeStore = useRecipeStore();
  const statsigEventsStore = useStatsigEventsStore();

  // Actions
  function updatePax(newPax: Pax, source: CoreEventSource): void {
    // TODO: Move this computed inside recipe state
    const hasSelectedRoom = recipeStore.hotelOnlyRecipe.some(
      (hotelIngredient) =>
        hotelIngredient.heartedHotels.some(
          (heartedHotel) =>
            !!heartedHotel.rooms?.length || !!heartedHotel.roomCodes?.length
        )
    );
    statsigEventsStore.logCorePaxUpdated(hasSelectedRoom, { source });
    const updatedPax = new TripPlannerPax(newPax);

    recipeStore.updatePax(updatedPax);
    savePaxInKeyValueStorage(
      updatedPax.rooms,
      browserStore.setSessionStorageItem
    );
    recipeStore.removeAllSelectedRooms();

    setNumberOfFerryVehiclesUpdatedWarning(
      updatedPax.totalNumberOfAdults,
      recipeStore.ferryOnlyRecipe
    );
    recipeStore.updateNumberOfFerryVehiclesBaseOnAdults(
      updatedPax.totalNumberOfAdults
    );
  }

  function clearWarningMessages(): void {
    shouldShowNumberOfVehiclesUpdatedWarning.value = false;
  }

  // Getters
  const paxesChildrenAgesText = computed<string | undefined>(() =>
    getFormattedChildrenAges(recipeStore.pax.rooms)
  );

  const getPaxesButtonSimpleText = computed<string>(() =>
    getFormattedPax(recipeStore.pax.rooms, false)
  );
  const getPaxesButtonDetailedText = computed<string>(() =>
    getFormattedPax(recipeStore.pax.rooms, true)
  );

  const shouldShowTicketsWarning = computed<boolean>(
    () => recipeStore.doesAnyStayHaveSubingredients
  );

  const shouldShowRoomSelectionWarning = computed<boolean>(() =>
    recipeStore.hotelOnlyRecipe.some((hotel) => hotel.heartedHotels.length)
  );

  const initialPaxes = computed<RoomPaxConfig[]>(() =>
    recipeStore.pax.tripPlannerRooms.map((room: TripPlannerRoomPax) => ({
      amountOfAdults: room.numberOfAdultsInRoom,
      amountOfChildren: room.numberOfChildrenInRoom,
      childAges: room.childAgesInRoom,
    }))
  );

  const numberOfAdults = computed<number>(() =>
    initialPaxes.value.reduce(
      (acc: number, room: RoomPaxConfig) => (acc += room.amountOfAdults),
      0
    )
  );

  const numberOfTravelers = computed<number>(() =>
    initialPaxes.value.reduce(
      (acc: number, room: RoomPaxConfig) =>
        (acc += room.amountOfAdults + room.amountOfChildren),
      0
    )
  );
  const shouldShowNumberOfVehiclesUpdatedWarning = ref<boolean>(false);
  const shouldShowNumberOfVehiclesWillBeUpdatedWarning = computed<boolean>(() =>
    recipeStore.ferryOnlyRecipe.some((ferry) => ferry.vehicles.length > 1)
  );

  // Helper functions
  function setNumberOfFerryVehiclesUpdatedWarning(
    totalNumberOfAdults: number,
    ferryOnlyRecipe: FerryIngredient[]
  ): void {
    if (shouldShowNumberOfVehiclesUpdatedWarning.value) {
      return;
    }
    ferryOnlyRecipe.some((ferry) => {
      if (ferry.vehicles.length > totalNumberOfAdults) {
        shouldShowNumberOfVehiclesUpdatedWarning.value = true;
        return true;
      }
      return ferry.vehicles.length > totalNumberOfAdults;
    });
  }

  return {
    // Actions
    updatePax,
    clearWarningMessages,

    // Getters
    initialPaxes,
    numberOfAdults,
    numberOfTravelers,
    paxesChildrenAgesText,
    getPaxesButtonSimpleText,
    getPaxesButtonDetailedText,
    shouldShowTicketsWarning,
    shouldShowRoomSelectionWarning,
    shouldShowNumberOfVehiclesUpdatedWarning,
    shouldShowNumberOfVehiclesWillBeUpdatedWarning,
  };
});
